import React, { InputHTMLAttributes } from 'react';
import Element, { CommonProps } from '../Element/Element';
import SmallText from '../Typography/SmallText/SmallText';
import BodyText from '../Typography/BodyText/BodyText';
import Image from '../Image/Image';
import styles from './SelectionButton.module.scss';
import { CreateProps } from '../../types/utils/CreateProps';

export type SelectionButtonProps = CreateProps<
  {
    /** Unique specifier for the SelectionButton */
    id: string;
    /** Name of the form control. Submitted with the form as a name of value.
     * Needed together with value prop to activate radiobutton, that will have this prop as a name. */
    name?: InputHTMLAttributes<HTMLInputElement>['name'];
    /** Value of the form control. Submitted with the form as a value of name.
     * Needed together with name prop to activate radiobutton, that will have this prop as a value. */
    value?: InputHTMLAttributes<HTMLInputElement>['value'];
    /** Main value shown inside the SelectionButton */
    label?: string;
    /** Smaller description shown inside the SelectionButton */
    description?: string;
    /** Disables the SelectionButton  */
    disabled?: boolean;
    /** Visually shows that option is not available. SelectionButton is still clickable and focusable */
    notAvailable?: boolean;
    /** Visualization if option is selected */
    selected?: boolean;
    /** Enables smaller height variant of the SelectionButton */
    narrow?: boolean;
    /** Variant with dropshadow. Disabled options don't show the shadow. */
    withShadow?: boolean;
    /** Src is used to show image inside of button. In order to show image use src and alt props. */
    src?: string;
    /** Alt is used with image inside SelectionButton to display image name.  In order to show image use src and alt props. */
    alt?: string;
    /** Title prop is used with image as well to add native html title property to img tag. */
    title?: string;
    /** Onclick  handler */
    onClick: (
      event: React.SyntheticEvent<HTMLButtonElement, Event>,
      id: string
    ) => void;
  },
  CommonProps<'button'>,
  | 'tagName'
  | 'backgroundColor'
  | 'boxShadow'
  | 'color'
  | 'padding'
  | 'borderRadius'
  | 'tabIndex'
  | 'onClick'
>;

// Alias for backwards compatibility
export type SelectionButtonShape = SelectionButtonProps;

const SelectionButton: React.FunctionComponent<SelectionButtonProps> = ({
  disabled,
  notAvailable,
  onClick,
  className,
  selected,
  label,
  src,
  title,
  description,
  alt,
  narrow,
  children,
  withShadow,
  id,
  name,
  value,
  ...props
}) => {
  const classNames = [styles.selectionButton];

  if (disabled) {
    classNames.push(styles.selectionButton__disabled);
  }

  if (selected && !disabled) {
    classNames.push(styles.selectionButton__selected);
  }

  if (notAvailable) {
    classNames.push(styles.selectionButton__notAvailable);
  }

  if (className) {
    classNames.push(className as string);
  }
  /** Blur method is used, to remove focus style onClick or touchStart events.
   * So we don't get unnesessary focus on mobile or mouseClick. */
  const blur = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
  };

  return (
    <Element
      tagName="button"
      type="button"
      className={classNames}
      aria-disabled={disabled}
      backgroundColor={disabled ? 'gray100' : 'white'}
      boxShadow={withShadow && !disabled ? 'sm' : 'zero'}
      color={disabled || notAvailable ? 'gray400' : 'inherit'}
      padding={
        narrow
          ? { horizontal: 'md', vertical: 'xs' }
          : src
          ? { size: 'xs' }
          : { size: 'md' }
      }
      margin={src ? { size: 'xs' } : {}}
      borderRadius={{ size: 'sm' }}
      onMouseDown={(event: React.MouseEvent) => blur(event)}
      onTouchStart={(event: React.TouchEvent) => blur(event)}
      onClick={(event: React.SyntheticEvent<HTMLButtonElement, Event>) =>
        !disabled && onClick && onClick(event, id)
      }
      tabIndex={disabled ? -1 : 0}
      {...props}
    >
      {!!name && !!value && (
        <input
          checked={selected}
          tabIndex={-1}
          className={styles.selectionButton_hiddenRadioInput}
          type="radio"
          name={name}
          value={value}
          readOnly
        />
      )}
      {!!notAvailable && (
        <div className={styles.selectionButton_lineContainer}>
          <svg width="100%" height="100%" className={styles.lineImage}>
            <line
              x1={0}
              x2="100%"
              y1={0}
              y2="100%"
              strokeWidth="1"
              className={styles.line}
            />
          </svg>
        </div>
      )}
      {!!label && (
        <BodyText
          tagName="span"
          color={
            disabled || notAvailable
              ? 'gray400'
              : selected
              ? 'purple500'
              : 'black'
          }
          fontWeight="bold"
        >
          {label}
        </BodyText>
      )}
      {!!src && !!alt && (
        <Image
          style={{ width: '58px', height: '58px' }}
          src={src}
          alt={alt}
          title={title}
        />
      )}
      {!!description && (
        <SmallText
          color={
            disabled || notAvailable
              ? 'gray400'
              : selected
              ? 'purple500'
              : 'gray500'
          }
        >
          {description}
        </SmallText>
      )}
      {children}
    </Element>
  );
};

export default SelectionButton;
